import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import "./style.css";

export default function CreateYogaPost({ onClose, onPostCreated }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [images, setImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    shortDescription: "",
    postType: "class",
    level: "all_levels",
    yogaStyle: "hatha",
    duration: "",
    focus: [],
    bodyAreas: [],
    benefits: [],
    tags: "",
    equipment: [],
    asanas: [],
    safetyTips: [],
    visibility: "public"
  });

  const token = user?.token || localStorage.getItem("token");

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: Array.isArray(prev[field]) 
        ? prev[field].includes(value)
          ? prev[field].filter(v => v !== value)
          : [...prev[field], value]
        : [value]
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);

    // Criar previews
    const previews = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then(setImagePreview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      alert("Título e descrição são obrigatórios");
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();

      // Adicionar campos básicos
      Object.keys(formData).forEach(key => {
        if (Array.isArray(formData[key]) && formData[key].length > 0) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (typeof formData[key] === "string" && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (typeof formData[key] === "number" && formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Adicionar imagens
      images.forEach((image, index) => {
        formDataToSend.append(`images`, image);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/yoga/post`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      onPostCreated(response.data.data);
      alert("Post de yoga criado com sucesso!");
    } catch (error) {
      console.error("Erro ao criar post:", error);
      alert("Erro ao criar post: " + error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="yoga-create-modal-overlay" onClick={onClose}>
      <div className="yoga-create-modal" onClick={(e) => e.stopPropagation()}>
        <button className="yoga-create-close" onClick={onClose}>✕</button>

        <h2>Criar Post de Yoga</h2>

        <div className="yoga-create-tabs">
          <button 
            className={`tab-btn ${activeTab === "basic" ? "active" : ""}`}
            onClick={() => setActiveTab("basic")}
          >
            Informações Básicas
          </button>
          <button 
            className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Detalhes
          </button>
          <button 
            className={`tab-btn ${activeTab === "content" ? "active" : ""}`}
            onClick={() => setActiveTab("content")}
          >
            Conteúdo
          </button>
          <button 
            className={`tab-btn ${activeTab === "media" ? "active" : ""}`}
            onClick={() => setActiveTab("media")}
          >
            Mídia
          </button>
        </div>

        <form onSubmit={handleSubmit} className="yoga-create-form">
          {/* ABA: INFORMAÇÕES BÁSICAS */}
          {activeTab === "basic" && (
            <div className="yoga-form-section">
              <div className="form-group">
                <label>Título *</label>
                <input 
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleBasicChange}
                  placeholder="Ex: Aula de Yoga para Iniciantes"
                  maxLength="200"
                  required
                />
                <span className="char-count">{formData.title.length}/200</span>
              </div>

              <div className="form-group">
                <label>Descrição Breve</label>
                <input 
                  type="text"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleBasicChange}
                  placeholder="Resumo rápido em uma linha"
                  maxLength="300"
                />
              </div>

              <div className="form-group">
                <label>Descrição Completa *</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleBasicChange}
                  placeholder="Descreva sua aula de yoga em detalhes..."
                  rows="6"
                  maxLength="5000"
                  required
                />
                <span className="char-count">{formData.description.length}/5000</span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Post *</label>
                  <select 
                    name="postType"
                    value={formData.postType}
                    onChange={handleBasicChange}
                  >
                    <option value="class">Aula Completa</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="meditation">Meditação</option>
                    <option value="breathing">Respiração (Pranayama)</option>
                    <option value="sequence">Sequência</option>
                    <option value="challenge">Desafio</option>
                    <option value="tip">Dicas</option>
                    <option value="philosophy">Filosofia</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Nível *</label>
                  <select 
                    name="level"
                    value={formData.level}
                    onChange={handleBasicChange}
                  >
                    <option value="beginner">Iniciante</option>
                    <option value="intermediate">Intermediário</option>
                    <option value="advanced">Avançado</option>
                    <option value="all_levels">Todos os Níveis</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estilo de Yoga *</label>
                  <select 
                    name="yogaStyle"
                    value={formData.yogaStyle}
                    onChange={handleBasicChange}
                  >
                    <option value="hatha">Hatha</option>
                    <option value="vinyasa">Vinyasa</option>
                    <option value="ashtanga">Ashtanga</option>
                    <option value="yin">Yin</option>
                    <option value="restorative">Restaurativo</option>
                    <option value="kundalini">Kundalini</option>
                    <option value="bikram">Bikram</option>
                    <option value="hot_yoga">Hot Yoga</option>
                    <option value="power_yoga">Power Yoga</option>
                    <option value="iyengar">Iyengar</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Duração (minutos)</label>
                  <input 
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleBasicChange}
                    placeholder="30"
                    min="1"
                    max="300"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Visibilidade</label>
                <select 
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleBasicChange}
                >
                  <option value="public">Público</option>
                  <option value="followers_only">Apenas Seguidores</option>
                  <option value="private">Privado</option>
                </select>
              </div>
            </div>
          )}

          {/* ABA: DETALHES */}
          {activeTab === "details" && (
            <div className="yoga-form-section">
              <div className="form-group">
                <label>Foco da Prática:</label>
                <div className="checkbox-group">
                  {["flexibility", "strength", "balance", "relaxation", "stress_relief", "energy_boost", "core", "back_care"].map(focus => (
                    <label key={focus} className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={formData.focus.includes(focus)}
                        onChange={() => handleArrayChange("focus", focus)}
                      />
                      {focus === "flexibility" && "Flexibilidade"}
                      {focus === "strength" && "Força"}
                      {focus === "balance" && "Equilíbrio"}
                      {focus === "relaxation" && "Relaxamento"}
                      {focus === "stress_relief" && "Alívio do Estresse"}
                      {focus === "energy_boost" && "Energia"}
                      {focus === "core" && "Core"}
                      {focus === "back_care" && "Cuidados com as Costas"}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Áreas do Corpo Trabalhadas:</label>
                <div className="checkbox-group">
                  {["full_body", "upper_body", "lower_body", "core", "back", "shoulders", "hips", "legs"].map(area => (
                    <label key={area} className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={formData.bodyAreas.includes(area)}
                        onChange={() => handleArrayChange("bodyAreas", area)}
                      />
                      {area === "full_body" && "Corpo Inteiro"}
                      {area === "upper_body" && "Tronco Superior"}
                      {area === "lower_body" && "Tronco Inferior"}
                      {area === "core" && "Core"}
                      {area === "back" && "Costas"}
                      {area === "shoulders" && "Ombros"}
                      {area === "hips" && "Quadril"}
                      {area === "legs" && "Pernas"}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Equipamentos Necessários:</label>
                <div className="checkbox-group">
                  {["none", "mat", "block", "strap", "bolster", "blanket", "chair"].map(eq => (
                    <label key={eq} className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={formData.equipment.includes(eq)}
                        onChange={() => handleArrayChange("equipment", eq)}
                      />
                      {eq === "none" && "Nenhum"}
                      {eq === "mat" && "Tapete"}
                      {eq === "block" && "Bloco"}
                      {eq === "strap" && "Correia"}
                      {eq === "bolster" && "Almofada"}
                      {eq === "blanket" && "Manta"}
                      {eq === "chair" && "Cadeira"}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ABA: CONTEÚDO */}
          {activeTab === "content" && (
            <div className="yoga-form-section">
              <div className="form-group">
                <label>Tags (separe por vírgula)</label>
                <input 
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="yoga, flexibilidade, meditação"
                />
              </div>

              <div className="form-group">
                <label>Benefícios</label>
                <div className="benefits-input">
                  <input 
                    type="text"
                    id="benefit-input"
                    placeholder="Digite um benefício e pressione Enter"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          setFormData(prev => ({
                            ...prev,
                            benefits: [...prev.benefits, value]
                          }));
                          e.target.value = "";
                        }
                      }
                    }}
                  />
                </div>
                <div className="benefits-list">
                  {formData.benefits.map((benefit, idx) => (
                    <span key={idx} className="benefit-tag">
                      {benefit}
                      <button 
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          benefits: prev.benefits.filter((_, i) => i !== idx)
                        }))}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Dicas de Segurança</label>
                <div className="safety-input">
                  <input 
                    type="text"
                    id="safety-input"
                    placeholder="Digite uma dica de segurança e pressione Enter"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          setFormData(prev => ({
                            ...prev,
                            safetyTips: [...prev.safetyTips, value]
                          }));
                          e.target.value = "";
                        }
                      }
                    }}
                  />
                </div>
                <div className="safety-list">
                  {formData.safetyTips.map((tip, idx) => (
                    <span key={idx} className="safety-tag">
                      {tip}
                      <button 
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          safetyTips: prev.safetyTips.filter((_, i) => i !== idx)
                        }))}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ABA: MÍDIA */}
          {activeTab === "media" && (
            <div className="yoga-form-section">
              <div className="form-group">
                <label>Imagens</label>
                <input 
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
                <div className="image-preview-grid">
                  {imagePreview.map((preview, idx) => (
                    <div key={idx} className="image-preview">
                      <img src={preview} alt={`preview-${idx}`} />
                      <button 
                        type="button"
                        onClick={() => {
                          setImages(images.filter((_, i) => i !== idx));
                          setImagePreview(imagePreview.filter((_, i) => i !== idx));
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {imagePreview.length === 0 && (
                <div className="upload-hint">
                  <p>📸 Adicione imagens para tornar seu post mais atrativo</p>
                </div>
              )}
            </div>
          )}

          {/* Botões de Ação */}
          <div className="yoga-form-actions">
            <button 
              type="button" 
              className="btn-cancel"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {loading ? "Criando..." : "Criar Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
